import React from 'react';
import { useSelector } from 'react-redux';
import { Controller, useFormContext } from 'react-hook-form';
import {
  CustomRichTextField,
  AvatarUploader,
  ErrorMessage,
  PrimarySelect,
  PrimaryTextField,
  PrimaryRadio,
  CustomModal,
  PrimaryButton,
  SecondaryButton,
  PrimaryNumericField,
  PrimaryMultiSelect,
} from 'components';
import {
  Typography,
  FormControlLabel,
  Grid, RadioGroup, Box, Chip,
} from '@mui/material';
import { IOption } from 'interfaces';
import { UploadResponse, UploadScenarios } from 'interfaces/file-uploader';
import { selectCategories } from 'base/core/selectors';
import useFileUpload from 'base/core/hooks/use-file-upload';
import { selectAuthUser } from 'base/auth/selectors';
import { CurrencyTypes } from 'pages/sessions/interface';
import { getOptionsFromEnum } from 'utils/data-utils';
import { SubscriptionFrequency } from 'interfaces/common-enums';
import { getAvatarLetters } from 'utils/common-utils';
import { handleContentDataDelete } from 'utils/file-uploader';
import { IUpdateProfileForm } from '../../interface';
import VideoInput from '../video-input';

const GeneralInformationForm = () => {
  const {
    control,
    formState: { errors },
    setValue,
    getValues,
    watch,
  } = useFormContext<IUpdateProfileForm>();

  const categories = useSelector(selectCategories);
  const emailAddress = watch('email');

  const categoryOptions = React.useMemo(() => (Array.isArray(categories)
    ? categories.map(
      (i) => ({ id: i.id, value: i.id, text: i.name }),
    ) : []), [categories]);
  const currencyOptions: IOption[] = getOptionsFromEnum(CurrencyTypes);

  const authUser = useSelector(selectAuthUser);
  const [introVideoUrl, setIntroVideoUrl] = React.useState(getValues('introVideoUrl'));
  const [thankVideoUrl, setThankVideoUrl] = React.useState(getValues('thankVideoUrl'));
  const [rateVideoUrl, setRateVideoUrl] = React.useState(getValues('rateVideoUrl'));
  const [introVideoLoading, setIntroVideoLoading] = React.useState(false);
  const [thankVideoLoading, setThankVideoLoading] = React.useState(false);
  const [rateVideoLoading, setRateVideoLoading] = React.useState(false);
  const [removeVideo, setRemoveVideo] = React.useState({
    type: '',
    openConfirmModal: false,
  });
  const introVideoFileInput: any = React.useRef(null);
  const thankVideoFileInput: any = React.useRef(null);
  const rateVideoFileInput: any = React.useRef(null);
  const { progress, handleContentDataUpload } = useFileUpload();

  const isEmailChanged: boolean = React.useMemo(
    () => (emailAddress !== authUser.email),
    [emailAddress, authUser.email],
  );

  React.useEffect(() => {
    setIntroVideoUrl(authUser.attributes.introVideoUrl);
    setThankVideoUrl(authUser.attributes.thankVideoUrl);
    setRateVideoUrl(authUser.attributes.rateVideoUrl);
  }, [authUser]);

  const handleIntroVideoClick = () => {
    introVideoFileInput.current.click();
  };
  const handleThankVideoClick = () => {
    thankVideoFileInput.current.click();
  };
  const handleRateVideoClick = () => {
    rateVideoFileInput.current.click();
  };

  const handleProfileImg = async (res: UploadResponse) => {
    await handleContentDataDelete(authUser?.profileImgUrl, UploadScenarios.expertProfile);
    setValue('profileImgUrl', res.url);
  };

  const handleIntroVideo = async (event: any) => {
    setIntroVideoLoading(true);
    await handleContentDataDelete(introVideoUrl, UploadScenarios.expertVideo);
    const res = await handleContentDataUpload(event, getValues('introVideoUrl'), UploadScenarios.expertVideo);
    setTimeout(() => {
      setValue('introVideoUrl', res.url);
      setIntroVideoUrl(res.url);
      setIntroVideoLoading(false);
    }, 3000);
  };

  const handleThankVideo = async (event: any) => {
    setThankVideoLoading(true);
    await handleContentDataDelete(thankVideoUrl, UploadScenarios.expertVideo);
    const res = await handleContentDataUpload(event, getValues('thankVideoUrl'), UploadScenarios.expertVideo);
    setTimeout(() => {
      setValue('thankVideoUrl', res.url);
      setThankVideoUrl(res.url);
      setThankVideoLoading(false);
    }, 3000);
  };
  const handleRateVideo = async (event: any) => {
    setRateVideoLoading(true);
    await handleContentDataDelete(rateVideoUrl, UploadScenarios.expertVideo);
    const res = await handleContentDataUpload(event, getValues('rateVideoUrl'), UploadScenarios.expertVideo);
    setTimeout(() => {
      setValue('rateVideoUrl', res.url);
      setRateVideoUrl(res.url);
      setRateVideoLoading(false);
    }, 3000);
  };

  const [accountType, setAccountType] = React.useState(authUser.attributes.accountType);

  const handleAccountTypeChange = (event: any) => {
    setValue('accountType', event.target.value);
    setAccountType(event.target.value);
  };

  const handleRemoveVideo = () => {
    switch (removeVideo.type) {
      case 'introVideoUrl':
        setIntroVideoUrl('');
        setValue('introVideoUrl', '');
        setRemoveVideo({ type: '', openConfirmModal: false });
        break;
      case 'thankVideoUrl':
        setThankVideoUrl('');
        setValue('thankVideoUrl', '');
        setRemoveVideo({ type: '', openConfirmModal: false });
        break;
      case 'rateVideoUrl':
        setRateVideoUrl('');
        setValue('rateVideoUrl', '');
        setRemoveVideo({ type: '', openConfirmModal: false });
        break;
      default:
        break;
    }
  };

  return (
    <>
      <CustomModal
        open={removeVideo.openConfirmModal}
        onClose={() => {}}
      >
        <>
          <Typography variant="h5" sx={{ mb: 3 }}>
            Are you sure you want to remove?
          </Typography>
          <div className="row">
            <div className="col-12 col-md-6 mb-2 mb-md-0">
              <PrimaryButton
                fullWidth
                onClick={handleRemoveVideo}
              >
                Yes, Confirm
              </PrimaryButton>
            </div>
            <div className="col-12 col-md-6">
              <SecondaryButton fullWidth onClick={() => setRemoveVideo({ type: '', openConfirmModal: false })}>
                Cancel
              </SecondaryButton>
            </div>
          </div>
        </>
      </CustomModal>
      <div className="form-item">
        <Typography variant="h5" sx={{ mb: 2 }}>General information</Typography>
      </div>
      <div className="form-item">
        <AvatarUploader
          handleFile={handleProfileImg}
          currentImageUrl={authUser?.profileImgUrl}
          scenario={UploadScenarios.expertProfile}
          sx={{ width: 100, height: 100 }}
          alt={getAvatarLetters(authUser)}
        />
        <ErrorMessage>{errors?.profileImgUrl?.message}</ErrorMessage>
      </div>
      <div className="form-item">
        <Controller
          control={control}
          name="accountType"
          render={({ field }) => (
            <RadioGroup {...field} onChange={handleAccountTypeChange}>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>Account type</Typography>
              <Grid container justifyContent="start-flex">
                <Grid item>
                  <FormControlLabel value="public" control={<PrimaryRadio />} label="Public" disabled={authUser.attributes.accountType === 'subscription'} />
                </Grid>
                <Grid item>
                  <FormControlLabel value="subscription" control={<PrimaryRadio />} label="Subscription" disabled={authUser.attributes.accountType === 'subscription'} />
                </Grid>
              </Grid>

            </RadioGroup>
          )}
        />
        <ErrorMessage>{errors?.accountType?.message}</ErrorMessage>
      </div>
      {accountType === 'subscription'
        ? (
          <>
            <div className="form-item">
              <Controller
                control={control}
                name="subscriptionFee"
                render={({ field }) => (
                  <PrimaryNumericField
                    {...field}
                    label="Subscription fee"
                    decimalAllowed
                    min={0.5}
                    max={1000000}
                  />
                )}
              />
              <ErrorMessage>{errors?.subscriptionFee?.message}</ErrorMessage>
            </div>
            <div className="form-item">
              <Controller
                control={control}
                name="subscriptionFrequency"
                render={({ field }) => (
                  <RadioGroup {...field}>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>Subscription fee frequency</Typography>
                    <Grid container justifyContent="start-flex">
                      <Grid item>
                        <FormControlLabel value={SubscriptionFrequency.monthly} control={<PrimaryRadio />} label="Monthly" />
                      </Grid>
                      <Grid item>
                        <FormControlLabel value={SubscriptionFrequency.annually} control={<PrimaryRadio />} label="Annually" />
                      </Grid>
                      <Grid item>
                        <FormControlLabel value={SubscriptionFrequency.onetime} control={<PrimaryRadio />} label="One-time" />
                      </Grid>
                    </Grid>
                  </RadioGroup>
                )}
              />
              <ErrorMessage>{errors?.subscriptionFrequency?.message}</ErrorMessage>
            </div>
          </>
        )
        : ' '}
      <div className="row">
        <div className="col-md-6">
          <div className="form-item">
            <Controller
              control={control}
              name="firstName"
              render={({ field }) => <PrimaryTextField {...field} label="First name" />}
            />
            <ErrorMessage>{errors?.firstName?.message}</ErrorMessage>
          </div>
        </div>
        <div className="col-md-6">
          <div className="form-item">
            <Controller
              control={control}
              name="lastName"
              render={({ field }) => <PrimaryTextField {...field} label="Last name" />}
            />
            <ErrorMessage>{errors?.lastName?.message}</ErrorMessage>
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-md-6">
          {categoryOptions && categoryOptions.length > 0 && (
            <div className="form-item">
              <Typography variant="body1">Select categories</Typography>
              <Controller
                control={control}
                name="categories"
                render={({ field }) => (
                  <PrimaryMultiSelect
                    options={categoryOptions}
                    value={field.value}
                    onChange={(e: any) => {
                      field.onChange(e.target.value);
                    }}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected
                          .filter(
                            (value) => categoryOptions.find((rs) => rs.id === value),
                          )
                          .map((value) => (
                            <Chip key={value} label={categoryOptions.filter((skill) => skill.id === value)[0]?.text} color="primary" />
                          ))}
                      </Box>
                    )}
                    autoHeight
                  />
                )}
              />
              <ErrorMessage>
                {errors?.categories ? errors?.categories?.message : ' '}
              </ErrorMessage>
            </div>
          )}
        </div>
        <div className="col-md-6">
          <div className="form-item">
            <Controller
              control={control}
              defaultValue="USD"
              name="currency"
              render={({ field }) => (
                <PrimarySelect
                  {...field}
                  label="Preferred currency"
                  options={currencyOptions}
                />
              )}
            />
            <ErrorMessage>{errors?.currency?.message}</ErrorMessage>
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-md-12">
          <div className="form-item">
            <Typography variant="h5" sx={{ mt: 2, mb: 1 }}>Email</Typography>
            {isEmailChanged && (
              <Typography variant="body2" sx={{ mb: 1, color: '#b76363' }}>
                {/* eslint-disable-next-line max-len */}
                (You will be logged out of your account. Please check your inbox to verify your new email address and log back in.)
              </Typography>
            )}
            <Controller
              control={control}
              name="email"
              render={({ field }) => <PrimaryTextField {...field} />}
            />
            <ErrorMessage>{errors?.email?.message}</ErrorMessage>
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-md-12">
          <div className="form-item">
            <Controller
              control={control}
              name="bio"
              render={({ field }) => <CustomRichTextField {...field} defaultValue={authUser.bio} multiline rows={3} label="Bio" options={{ options: ['inline', 'list'], inline: { options: ['bold'] }, list: { options: ['indent'] } }} />}
            />
            <ErrorMessage>{errors?.bio?.message}</ErrorMessage>
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-md-4">
          <div>
            <Controller
              control={control}
              name="introVideoUrl"
              render={() => (
                <VideoInput
                  url={introVideoUrl}
                  handleInputClick={handleIntroVideoClick}
                  label="Intro video"
                  buttonLabel="Upload new intro video"
                  handleVideo={handleIntroVideo}
                  videoLoading={introVideoLoading}
                  videoFileInputRef={introVideoFileInput}
                  onRemove={() => { setRemoveVideo({ type: 'introVideoUrl', openConfirmModal: true }); }}
                />
              )}
            />
            <ErrorMessage>{errors?.introVideoUrl?.message}</ErrorMessage>
          </div>
        </div>
        <div className="col-md-4">
          <div>
            <Controller
              control={control}
              name="thankVideoUrl"
              render={() => (
                <VideoInput
                  url={thankVideoUrl}
                  handleInputClick={handleThankVideoClick}
                  label="Thank you video"
                  buttonLabel="Upload new thank you video"
                  handleVideo={handleThankVideo}
                  videoLoading={thankVideoLoading}
                  videoFileInputRef={thankVideoFileInput}
                  onRemove={() => { setRemoveVideo({ type: 'thankVideoUrl', openConfirmModal: true }); }}
                />
              )}
            />
            <ErrorMessage>{errors?.thankVideoUrl?.message}</ErrorMessage>
          </div>
        </div>
        <div className="col-md-4">
          <div>
            <Controller
              control={control}
              name="rateVideoUrl"
              render={() => (
                <VideoInput
                  url={rateVideoUrl}
                  handleInputClick={handleRateVideoClick}
                  label="Rating video"
                  buttonLabel="Upload new rating video"
                  handleVideo={handleRateVideo}
                  videoLoading={rateVideoLoading}
                  videoFileInputRef={rateVideoFileInput}
                  onRemove={() => { setRemoveVideo({ type: 'rateVideoUrl', openConfirmModal: true }); }}
                />
              )}
            />
            <ErrorMessage>{errors?.rateVideoUrl?.message}</ErrorMessage>
          </div>
        </div>
      </div>
    </>
  );
};

export default GeneralInformationForm;
